import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import t from '../../common/t';
export default class PhotoModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.index = this.attrs.index || 0;
    this.scrollPosition = {
      x: window.scrollX,
      y: window.scrollY
    };
    this.pointers = new Set();
    this.zoomed = (window.visualViewport?.scale || 1) > 1.01;
    this.gestureEvents = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'contextmenu', 'dragstart', 'wheel', 'scroll', 'gesturestart', 'dblclick', 'keydown'];
    this.trackGesture = event => this.observeGesture(event);
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.document = this.element.ownerDocument;
    // Observe without preventing native scrolling, zooming or the image menu.
    for (const type of this.gestureEvents) this.document.addEventListener(type, this.trackGesture, {
      capture: true,
      passive: true
    });
    this.viewport = window.visualViewport;
    this.trackZoom = () => {
      this.cancelTap();
      this.zoomed = (this.viewport.scale || 1) > 1.01;
      m.redraw();
    };
    this.viewport?.addEventListener('resize', this.trackZoom, {
      passive: true
    });
    // Fullscreen phones need no opening transition, so transitionend is not
    // guaranteed. Run after the manager's native scroll-lock animation frame.
    this.readyFrame = window.requestAnimationFrame(() => this.preserveReadingPosition());
  }
  onready() {
    super.onready();
    this.preserveReadingPosition();
  }
  preserveReadingPosition() {
    const body = this.document?.body;
    // The native iOS scroll lock needs CSS lengths to restore the reading position.
    if (body?.style.position === 'fixed') {
      body.style.top = `${-this.scrollPosition.y}px`;
      body.style.left = `${-this.scrollPosition.x}px`;
    }
  }
  // Photo taps must not wait for the native modal's closing animation or its
  // additional 300ms removal delay. The manager still owns focus/scroll unlock.
  hide() {
    if (this.closing) return;
    this.closing = true;
    this.cancelTap();
    this.preserveReadingPosition();
    this.attrs.state.close();
  }
  onbeforeremove() {}
  onremove(vnode) {
    this.cancelTap();
    for (const type of this.gestureEvents) this.document?.removeEventListener(type, this.trackGesture, true);
    this.viewport?.removeEventListener('resize', this.trackZoom);
    window.cancelAnimationFrame(this.readyFrame);
    super.onremove(vnode);
  }
  cancelTap() {
    this.tap = null;
    this.closeOnClick = false;
  }
  observeGesture(event) {
    if (event.type === 'pointerdown') {
      this.cancelTap();
      this.pointers.add(event.pointerId);
      const image = this.element.querySelector('.CCOT-photo-content')?.contains(event.target);
      if (image && !this.zoomed && this.pointers.size === 1 && event.button === 0 && event.isPrimary !== false && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        this.tap = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          time: event.timeStamp,
          moved: false
        };
      }
    } else if (event.type === 'pointermove' || event.type === 'pointerup') {
      const tap = this.tap;
      if (tap && tap.id === event.pointerId) {
        const dx = event.clientX - tap.x,
          dy = event.clientY - tap.y,
          duration = event.timeStamp - tap.time;
        if (Math.hypot(dx, dy) > 8) tap.moved = true;
        if (event.pointerType === 'mouse' && event.type === 'pointermove' && event.buttons !== 1) this.cancelTap();
        if (event.type === 'pointerup') {
          const single = !!this.tap && this.pointers.size === 1 && !this.zoomed;
          if (single && ['touch', 'pen'].includes(event.pointerType) && duration < 1000 && Math.abs(dy) >= 48 && Math.abs(dy) > Math.abs(dx) * 1.5) {
            this.move(dy < 0 ? 1 : -1);
            m.redraw();
          } else this.closeOnClick = single && !tap.moved && duration < 400;
          this.readyAt = event.timeStamp;
          this.tap = null;
        }
      }
      if (event.type === 'pointerup') this.pointers.delete(event.pointerId);
    } else {
      if (event.type === 'pointercancel') this.pointers.delete(event.pointerId);
      this.cancelTap();
      if (event.type === 'keydown' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          this.move(1);
          m.redraw();
        }
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          this.move(-1);
          m.redraw();
        }
      }
    }
  }
  closeImage(event) {
    event.stopPropagation();
    if (event.detail === 0 && !event.pointerType) {
      this.cancelTap();
      this.hide();
      return;
    }
    const close = this.closeOnClick && event.detail === 1 && event.timeStamp - this.readyAt < 750;
    this.closeOnClick = false;
    if (close) this.hide();
  }
  current() {
    return this.attrs.images?.[this.index] || this.attrs;
  }
  move(step) {
    this.cancelTap();
    const next = this.index + step;
    if (!this.attrs.images || next < 0 || next >= this.attrs.images.length) return;
    this.index = next;
    this.error = false;
  }
  className() {
    return 'CCOT-photo-modal' + (this.zoomed ? ' CCOT-photo-zoomed' : '') + (this.current().caption ? ' CCOT-photo-with-caption' : '');
  }
  title() {
    return t('lib.post_images_preview', {
      number: this.attrs.images ? this.index + 1 : this.attrs.number || 1
    });
  }
  content() {
    const image = this.current();
    return m('.Modal-body', [!this.error && m('.CCOT-photo-content', {
      onclick: event => this.closeImage(event)
    }, [m('button.CCOT-photo-close', {
      type: 'button',
      title: t('lib.post_images_close'),
      'aria-label': t('lib.post_images_close'),
      onclick: event => this.closeImage(event)
    }, m('img', {
      key: image.image,
      src: image.image,
      alt: this.title(),
      draggable: false,
      onerror: () => {
        if (this.current().image === image.image) {
          this.error = true;
          m.redraw();
        }
      }
    }))]), this.error && m('.CCOT-photo-error', [m('p', {
      role: 'alert'
    }, t('lib.post_images_blocked')), m(Button, {
      type: 'button',
      className: 'Button',
      onclick: () => this.hide()
    }, t('lib.post_images_close'))]), image.caption && m('p.CCOT-help.CCOT-photo-caption', image.caption), this.attrs.images?.length > 1 && m('.CCOT-photo-navigation', [m(Button, {
      type: 'button',
      className: 'Button',
      disabled: this.index === 0,
      onclick: () => this.move(-1)
    }, t('lib.post_images_previous_photo')), m('span', t('lib.post_images_preview_position', {
      current: this.index + 1,
      total: this.attrs.images.length
    })), m(Button, {
      type: 'button',
      className: 'Button',
      disabled: this.index + 1 === this.attrs.images.length,
      onclick: () => this.move(1)
    }, t('lib.post_images_next_photo'))])]);
  }
}
_defineProperty(PhotoModal, "isDismissibleViaCloseButton", false);
flarum.reg.add('bushcraftcn-ccot', 'forum/postImages/PhotoModal', PhotoModal);